import {
  type AppError,
  InvalidTokenError,
  NotAuthorizedError,
  type ResultWithOauthToken,
} from '@packages/utils';
import {
  cookieApiRefreshTokenKey,
  cookieApiTokenKey,
} from '../../../server/token';
import {
  checkAccessTokenExistence,
  checkRefreshTokenExistence,
} from './checkOauthTokenExistence';
import {
  type HandleRefreshAccessTokenArgs,
  SESSION_EXPIRED_RETURN_VALUE,
  handleRefreshAccessToken,
} from './handleRefreshAccessToken';

type SendApiRequestArgs<T> = HandleRefreshAccessTokenArgs<T> & {
  authRequired?: boolean;
  allowReturnError?: boolean;
};

// セッション切れは呼び出し側がInvalidTokenErrorで分岐してUIを出し分けるため、
// allowReturnErrorの指定によらずthrowせずに返す
const handleResult = <T>(
  result: ResultWithOauthToken<T, AppError>,
  allowReturnError: boolean,
): ResultWithOauthToken<T, AppError> => {
  if (!result.ok && !allowReturnError && result.error !== InvalidTokenError) {
    throw new Error(result.error);
  }

  return result;
};

// Cookieはクライアントサイドとサーバーサイドで取得方法が違うため、
// 呼び出し箇所で引数で呼び出す必要がある
export const sendApiRequest = async <T>({
  cookies,
  authRequired = true,
  request,
  version,
  allowReturnError = false,
}: SendApiRequestArgs<T>): Promise<ResultWithOauthToken<T, AppError>> => {
  const refreshAndRetry = async () =>
    handleResult(
      await handleRefreshAccessToken({cookies, request, version}),
      allowReturnError,
    );

  const {isValid: isValidRefreshToken} = checkRefreshTokenExistence(
    cookies[cookieApiRefreshTokenKey],
  );
  const {isValid: isValidAccessToken, providedAccessToken} =
    checkAccessTokenExistence(cookies[cookieApiTokenKey]);

  // 未ログイン状態とアクセストークンが無効な場合が、同じ条件で判定になっている？
  if (authRequired && !isValidRefreshToken) {
    return SESSION_EXPIRED_RETURN_VALUE;
  }

  if (!isValidAccessToken && isValidRefreshToken) {
    return refreshAndRetry();
  }

  const result = await request(providedAccessToken);
  if (!result.ok && result.error === NotAuthorizedError) {
    return refreshAndRetry();
  }

  return handleResult(result, allowReturnError);
};
